/**
 * Rebuy Smart Cart drawer enhancements (Figma 491:2437 / 491:6716).
 * Does not restyle .rebuy-cart__flyout-recommendations internals; it only
 * corrects the screen-reader markup of Rebuy product cards (tidyProductCards).
 */
class RebuyCartEnhancer extends HTMLElement {
  connectedCallback() {
    if (this._started) return;
    this._started = true;

    this.assets = {
      eye: this.dataset.eye,
      flower: this.dataset.flower,
    };

    // Rebuy renders client-side, so every string this file writes comes in
    // pre-translated on the element rather than being spelled out here.
    this.strings = {
      subtotal: this.dataset.subtotal,
      taxesNote: this.dataset.taxesNote,
      itemCountOne: this.dataset.itemCountOne,
      itemCountOther: this.dataset.itemCountOther,
      unitPrice: this.dataset.unitPrice,
      giftPoweredBy: this.dataset.giftPoweredBy,
    };

    this._onSchedule = this.schedule.bind(this);
    this._scheduled = false;

    this._onRemoveClick = this.onRemoveClick.bind(this);
    document.addEventListener('click', this._onRemoveClick, true);

    this._onImageClick = this.onImageClick.bind(this);
    document.addEventListener('click', this._onImageClick);

    this.enhance();

    this._observer = new MutationObserver(this._onSchedule);
    this._observer.observe(document.documentElement, { childList: true, subtree: true });

    // Watches Rebuy widgets going inert and the Smart Cart opening/closing,
    // for releaseStaleWidgetInert
    var self = this;
    this._inertObserver = new MutationObserver(function (records) {
      var relevant = records.some(function (record) {
        return record.target.id === 'rebuy-cart' || (record.attributeName === 'inert' && record.target.classList.contains('rebuy-widget'));
      });
      if (relevant) self.schedule();
    });
    this._inertObserver.observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['inert', 'aria-hidden'],
    });

    document.addEventListener('rebuy.smartcart.show', this._onSchedule);
    document.addEventListener('rebuy.smartcart.ready', this._onSchedule);
    document.addEventListener('rebuy.smartcart.change', this._onSchedule);
    document.addEventListener('rebuy:cart.change', this._onSchedule);
    window.addEventListener('rebuy.cart.change', this._onSchedule);
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    if (this._inertObserver) this._inertObserver.disconnect();
    document.removeEventListener('click', this._onRemoveClick, true);
    document.removeEventListener('click', this._onImageClick);
    document.removeEventListener('rebuy.smartcart.show', this._onSchedule);
    document.removeEventListener('rebuy.smartcart.ready', this._onSchedule);
    document.removeEventListener('rebuy.smartcart.change', this._onSchedule);
    document.removeEventListener('rebuy:cart.change', this._onSchedule);
    window.removeEventListener('rebuy.cart.change', this._onSchedule);
  }

  onRemoveClick(event) {
    var btn = event.target.closest && event.target.closest('.rebuy-cart__flyout-item-remove');
    if (!btn || btn.closest('.rebuy-cart__flyout-recommendations')) return;
    btn.classList.add('is-removing');
    btn.setAttribute('aria-busy', 'true');
  }

  // An inert element is skipped by hit testing, so a tap on a card's picture
  // lands on the media box around it. Taps inside the picture's area are
  // passed to the title link (same product page, and Rebuy's click tracking
  // runs on it); the "+ Add" button and badge over the picture keep their own.
  onImageClick(event) {
    var media = event.target.closest && event.target.closest('.rebuy-product-media');
    if (!media || event.target.closest('button, a, input, label')) return;

    var imageLink = media.querySelector('a.rebuy-product-image');
    if (!imageLink || !imageLink.inert) return;

    var rect = imageLink.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) return;

    var card = media.closest('.rebuy-product-block');
    var titleLink = card && card.querySelector('.rebuy-product-title-link[href]');
    if (titleLink) titleLink.click();
  }

  flyout() {
    return document.querySelector('.rebuy-cart__flyout');
  }

  smartCart() {
    return window.SmartCart || (window.Rebuy && window.Rebuy.SmartCart) || null;
  }

  itemCount() {
    var cart = this.smartCart();
    if (cart && typeof cart.itemCount === 'function') {
      return Number(cart.itemCount()) || 0;
    }
    if (typeof window.SmartCart === 'object' && typeof window.SmartCart.itemCount === 'function') {
      return Number(window.SmartCart.itemCount()) || 0;
    }
    var total = 0;
    document.querySelectorAll('.rebuy-cart__flyout-item-quantity-widget-label').forEach(function (el) {
      var n = parseInt(el.textContent.replace(/[^\d]/g, ''), 10);
      if (!isNaN(n)) total += n;
    });
    return total;
  }

  subtotalText() {
    var box = document.querySelector('.rebuy-cart__flyout-subtotal-amount');
    if (!box) return '';

    var clone = box.cloneNode(true);
    clone.querySelectorAll('.sr-only, [aria-hidden="true"], s, del, .compare-at, .rebuy-money--compare').forEach(function (el) {
      el.parentNode.removeChild(el);
    });

    var money = clone.querySelector('.rebuy-money, .money');
    var source = money ? money.textContent : clone.textContent;
    var match = String(source)
      .replace(/Final price|Original price|Compare at/gi, ' ')
      .match(/\$[\d,]+(?:\.\d{2})?/);
    return match ? match[0] : '';
  }

  ensureHeader(root) {
    var header = root.querySelector('.rebuy-cart__flyout-header');
    var titleWrap = header && header.querySelector('div');
    var title = root.querySelector('.rebuy-cart__flyout-title');
    if (!header || !titleWrap || !title) return;

    if (this.assets.eye && !titleWrap.querySelector('.fs-rebuy-cart-eye')) {
      var eye = document.createElement('img');
      eye.className = 'fs-rebuy-cart-eye';
      eye.src = this.assets.eye;
      eye.alt = '';
      eye.setAttribute('aria-hidden', 'true');
      titleWrap.insertBefore(eye, title);
    }

    if (!titleWrap.querySelector('.fs-rebuy-cart-count')) {
      var count = document.createElement('span');
      count.className = 'fs-rebuy-cart-count';
      title.insertAdjacentElement('afterend', count);
    }
  }

  // Setting textContent always swaps the text node, even to the same string,
  // and that mutation re-triggers the document observer: writing every pass
  // re-ran enhance() on every animation frame for as long as the page was open.
  setText(el, text) {
    if (el.textContent !== text) el.textContent = text;
  }

  updateCount(root) {
    var el = root.querySelector('.fs-rebuy-cart-count');
    if (!el) return;
    var n = this.itemCount();
    var template = n === 1 ? this.strings.itemCountOne : this.strings.itemCountOther;
    this.setText(el, template ? template.replace('__count__', n) : '');
  }

  formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: currency,
    }).format(cents / 100);
  }

  // Rebuy's own price element shows the unit price only, so both the
  // "$98.00 each" line and the line total are written from the cart data.
  ensureLinePrices(root) {
    var cart = this.smartCart();
    if (!this.strings.unitPrice || !cart || typeof cart.items !== 'function') return;

    var items = cart.items() || [];
    var rows = root.querySelectorAll('.rebuy-cart__flyout-items > .rebuy-cart__flyout-item');
    // Rows render in cart order; when the counts disagree, skip rather than label the wrong line.
    if (rows.length !== items.length) return;

    var self = this;
    rows.forEach(function (row, index) {
      var info = row.querySelector('.rebuy-cart__flyout-item-info');
      if (!info) return;

      var unit = info.querySelector('.fs-rebuy-unit-price');
      if (!unit) {
        unit = document.createElement('p');
        unit.className = 'fs-rebuy-unit-price';
        info.appendChild(unit);
      }

      var text = self.strings.unitPrice.replace('__price__', self.formatMoney(items[index].final_price));
      if (unit.textContent !== text) unit.textContent = text;

      var total = info.querySelector('.fs-rebuy-line-total');
      if (!total) {
        total = document.createElement('p');
        total.className = 'fs-rebuy-line-total';
        info.appendChild(total);
      }

      var totalText = self.formatMoney(items[index].final_line_price);
      if (total.textContent !== totalText) total.textContent = totalText;
    });
  }

  cartIsEmpty() {
    var cart = this.smartCart();
    if (cart && typeof cart.hasItems === 'function') {
      return !cart.hasItems();
    }
    return this.itemCount() === 0;
  }

  syncEmptyState(root) {
    var empty = this.cartIsEmpty() || !!root.querySelector('.rebuy-cart__flyout-content.no-items');
    root.classList.toggle('fs-rebuy-cart-is-empty', empty);
  }

  ensureProgress(root) {
    var meter = root.querySelector('.rebuy-cart__progress-bar-meter');
    var wrapper = root.querySelector('.rebuy-cart__progress-bar-wrapper');
    if (meter && this.assets.flower && !meter.querySelector('.fs-rebuy-progress-flower')) {
      var flower = document.createElement('img');
      flower.className = 'fs-rebuy-progress-flower';
      flower.src = this.assets.flower;
      flower.alt = '';
      flower.setAttribute('aria-hidden', 'true');
      meter.appendChild(flower);
    }

    if (wrapper && !wrapper.querySelector('.fs-rebuy-progress-total')) {
      var total = document.createElement('div');
      total.className = 'fs-rebuy-progress-total';
      wrapper.appendChild(total);
    }

    var prompt = root.querySelector('.rebuy-cart__progress-bar-prompt');
    if (prompt) this.normalizePrompt(prompt);
  }

  normalizePrompt(prompt) {
    var html = prompt.innerHTML;
    html = html.replace(/You are/gi, "You're");
    html = html.replace(/You have unlocked/gi, "You've unlocked");
    html = html.replace(/You've unlock(?!ed)/gi, "You've unlocked");
    html = html.replace(/\s*Congratulations!?\.?\s*/gi, ' ');
    if (!/<b>|<strong>/i.test(html) && /FREE\s*SHIPPING/i.test(html)) {
      html = html.replace(/FREE\s*SHIPPING/i, '<b>FREE SHIPPING</b>');
    }
    if (html !== prompt.innerHTML) prompt.innerHTML = html;
  }

  updateProgress(root) {
    var fill = root.querySelector('.rebuy-cart__progress-bar-meter-fill');
    var flower = root.querySelector('.fs-rebuy-progress-flower');
    if (fill && flower) {
      var width = fill.style.width || getComputedStyle(fill).width;
      flower.style.left = width;
    }

    var total = root.querySelector('.fs-rebuy-progress-total');
    if (total) this.setText(total, this.subtotalText());
  }

  cleanGiftNote(root) {
    root.querySelectorAll('.rebuy-cart__flyout-integration').forEach(function (box) {
      var nodes = [box].concat(Array.prototype.slice.call(box.querySelectorAll('*')));
      nodes.forEach(function (el) {
        Array.prototype.slice.call(el.childNodes).forEach(function (node) {
          if (node.nodeType !== 3) return;
          if (/rebuy-cart__flyout|!important/.test(node.textContent)) {
            node.parentNode.removeChild(node);
          }
        });
      });
    });

    var giftStyle = document.querySelector('#giftnote-rebuycart style');
    if (giftStyle && !giftStyle.getAttribute('data-fs-gift')) {
      giftStyle.textContent =
        '#button{' +
        'font-family:Jost,sans-serif!important;' +
        '-webkit-appearance:none;' +
        'appearance:none;' +
        'display:inline-block;' +
        'width:auto;' +
        'max-width:100%;' +
        'margin:0;' +
        'padding:0;' +
        'border:0!important;' +
        'border-radius:0;' +
        'background:transparent!important;' +
        'box-shadow:none!important;' +
        'color:#000!important;' +
        'font-weight:600!important;' +
        'font-size:14px!important;' +
        'letter-spacing:.3px;' +
        'line-height:1.5;' +
        'text-align:left;' +
        'text-transform:uppercase;' +
        'cursor:pointer;' +
        '}' +
        '#button:hover{opacity:.7;}';
      giftStyle.setAttribute('data-fs-gift', 'true');
    }
  }

  moveGiftNote(root) {
    var gift = root.querySelector('.rebuy-cart__flyout-footer .rebuy-cart__flyout-integration, .rebuy-cart__flyout-integration');
    var recs = root.querySelector('.rebuy-cart__flyout-recommendations');
    var content = root.querySelector('.rebuy-cart__flyout-content');
    if (!gift || !content) return;
    if (recs && gift.nextElementSibling !== recs) {
      content.insertBefore(gift, recs);
    } else if (!recs && gift.parentElement !== content) {
      content.appendChild(gift);
    }
  }

  ensureGiftCredit(root) {
    var gift = root.querySelector(
      '.rebuy-cart__flyout-content > .rebuy-cart__flyout-integration, .rebuy-cart__flyout-body > .rebuy-cart__flyout-integration'
    );
    if (!gift || !this.strings.giftPoweredBy || gift.querySelector('.fs-rebuy-gift-credit')) return;

    var credit = document.createElement('span');
    credit.className = 'fs-rebuy-gift-credit';

    var label = document.createElement('span');
    label.textContent = this.strings.giftPoweredBy;

    var brand = document.createElement('span');
    brand.className = 'fs-rebuy-gift-credit__brand';
    brand.textContent = 'giftnote';

    credit.appendChild(label);
    credit.appendChild(brand);
    gift.appendChild(credit);
  }

  ensureFooter(root) {
    var footer = root.querySelector('.rebuy-cart__flyout-footer');
    if (!footer) return;

    // Rebuy appends its own "(3 items)" to the subtotal label; the count already
    // sits in the header, so the label is reduced back to the bare word.
    var label = footer.querySelector('.rebuy-cart__flyout-subtotal-label span');
    if (label && this.strings.subtotal && label.textContent.indexOf('(') !== -1) {
      this.setText(label, this.strings.subtotal);
    }

    // The dashboard "ready" callback strips the leading "or" only on open and
    // show; Rebuy re-renders the line on every cart change, so it is repeated here.
    var installments = footer.querySelector('.rebuy-cart__flyout-installments');
    var lead = installments && installments.firstChild;
    if (lead && lead.nodeType === 3 && /^\s*or\s+/i.test(lead.textContent)) {
      lead.textContent = lead.textContent.replace(/^\s*or\s+/i, '');
    }

    if (this.strings.taxesNote && !footer.querySelector('.fs-rebuy-cart-disclaimer')) {
      var note = document.createElement('p');
      note.className = 'fs-rebuy-cart-disclaimer';
      note.textContent = this.strings.taxesNote;
      footer.appendChild(note);
    }
  }

  /**
   * Rebuy product cards (cart drawer recommendations, PDP and homepage bundle
   * widgets) named the product at every VoiceOver stop: the image link, its
   * image, the "View <title>" title link, the heading around it, and a bundle
   * checkbox's label text read apart from the checkbox. Swiping through four
   * cards ran a dozen-plus names together, so the user could not tell which
   * product a control belonged to. Each card is reduced to one stop per
   * control. Rebuy re-renders its cards, so this runs on every pass and only
   * writes what differs.
   */
  tidyProductCards() {
    document.querySelectorAll('.rebuy-product-block').forEach(function (card, index) {
      // A bare "product" label on every list item adds noise, not a name
      if (card.getAttribute('aria-label') === 'product') card.removeAttribute('aria-label');

      // The image link repeats the title link. It is made inert rather than
      // aria-hidden, which would leave a focusable link with no name; taps on
      // the picture still reach the product page through onImageClick.
      var imageLink = card.querySelector('a.rebuy-product-image');
      if (imageLink && !imageLink.inert && card.querySelector('.rebuy-product-title-link[href]')) {
        imageLink.inert = true;
      }

      // "View <title>" replaced the link's visible text, and named the heading with it
      var titleLink = card.querySelector('.rebuy-product-title-link[aria-label]');
      if (titleLink && titleLink.textContent.trim()) titleLink.removeAttribute('aria-label');

      // "product star rating" was announced as a stop of its own ahead of the
      // rating it wraps, which already reads "4.9 stars out of 5 stars"
      var review = card.querySelector('.rebuy-product-review[aria-label]');
      if (review) review.removeAttribute('aria-label');

      // The screen-reader-only "Price" word is a separate VoiceOver stop beside
      // the amount, so the price was heard twice; the amount reads on its own.
      // Attributes only: Rebuy re-renders these nodes and owns their text. Sale
      // cards keep their labels, which tell the sale and original price apart.
      if (!card.querySelector('.rebuy-money.compare-at')) {
        card.querySelectorAll('.rebuy-money > .sr-only').forEach(function (word) {
          if (word.getAttribute('aria-hidden') !== 'true') word.setAttribute('aria-hidden', 'true');
        });
      }

      // Bundle checkbox: point its name at the label text, then hide that text
      // as a separate stop. A reference (not a copy) keeps the name right when
      // Rebuy swaps the product in the same card.
      var checkbox = card.querySelector('.rebuy-checkbox-label input[type="checkbox"]');
      var labelText = checkbox && checkbox.parentNode.querySelector('.sr-only');
      if (labelText) {
        if (!labelText.id) labelText.id = 'RebuyCheckboxLabel-' + (checkbox.id || index);
        if (checkbox.getAttribute('aria-labelledby') !== labelText.id) {
          checkbox.setAttribute('aria-labelledby', labelText.id);
        }
        if (labelText.getAttribute('aria-hidden') !== 'true') labelText.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /**
   * Adding to cart from one Rebuy widget ("Pair it with") makes Rebuy set
   * inert on the page's other Rebuy widgets ("We think you'll love these
   * too"), and it never removes it. Inert content is skipped by VoiceOver and
   * the keyboard, so the rest of the product page was unreachable until a
   * refresh. While the Smart Cart is closed no widget should be inert; while it
   * is open the page behind it is already inert (modal background guard).
   */
  releaseStaleWidgetInert() {
    var cart = document.getElementById('rebuy-cart');
    if (cart && cart.getAttribute('aria-hidden') === 'false') return;
    document.querySelectorAll('.rebuy-widget[inert]').forEach(function (widget) {
      widget.removeAttribute('inert');
    });
  }

  enhance() {
    this.releaseStaleWidgetInert();
    this.tidyProductCards();

    var root = this.flyout();
    if (!root) return;

    this.ensureHeader(root);
    this.updateCount(root);
    this.ensureLinePrices(root);
    this.syncEmptyState(root);
    this.ensureProgress(root);
    this.updateProgress(root);
    this.moveGiftNote(root);
    this.cleanGiftNote(root);
    this.ensureGiftCredit(root);
    this.ensureFooter(root);
  }

  schedule() {
    if (this._scheduled) return;
    this._scheduled = true;
    var self = this;
    requestAnimationFrame(function () {
      self._scheduled = false;
      self.enhance();
    });
  }
}

if (!customElements.get('rebuy-cart-enhancer')) {
  customElements.define('rebuy-cart-enhancer', RebuyCartEnhancer);
}
